// Field schema for the **green-fee** rate-line extraction specialist.
//
// Hard-coded source of truth for the structured payload we expect the
// green-fee specialist to emit. The Extraction modal renders directly
// from these constants; the parsing prompt and downstream consumers
// should also reference them so a single edit here propagates everywhere.
//
// Sibling files:
// - ./green-fees-parameters.js — prompts that drive the specialist
// - ./accommodation-schema.js — the parallel specialist's schema

export const GREEN_FEES_FIELD_SCHEMA = [
	{
		key: "extraction_id",
		type: "string (UUID)",
		required: true,
		description: "Unique identifier for this extraction run.",
		agent_note: "Generate a UUID v4 string. Each extraction call should be uniquely traceable.",
	},
	{
		key: "extracted_at",
		type: "ISO datetime",
		required: true,
		description: "Timestamp recording when this extraction run completed.",
		agent_note: "ISO 8601 with timezone, UTC preferred. Set from the system clock at output time.",
	},
	{
		key: "green_fee_lines[].course",
		type: "string",
		required: true,
		description: "Course name exactly as printed on the rate card. Verbatim, including diacritics and apostrophes (\"Faldo Course\", \"O'Connor Jnr\", \"António Pina Atanas Par 3\").",
		agent_note: "Capture exactly as written. Do not translate, paraphrase, or normalise. The downstream search hooks will match on these strings.",
	},
	{
		key: "green_fee_lines[].rounds",
		type: "number",
		required: true,
		description: "Number of rounds this rate covers. Default 1.",
		agent_note: "Integer count. Use 1 unless the rate card explicitly bundles multiple rounds into a single price (\"2 rounds for €280\" → 2).",
	},
	{
		key: "green_fee_lines[].holes",
		type: "number",
		required: true,
		description: "Number of holes per round. Default 18.",
		agent_note: "Integer count. Default 18. Use 9 when the rate is explicitly for a 9-hole round.",
	},
	{
		key: "green_fee_lines[].tee_window",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "anytime", description: "Single rate covering all tee times." },
			{ value: "morning", description: "Early/AM tee slots (e.g. before 12:00)." },
			{ value: "afternoon", description: "Midday/PM tee slots." },
			{ value: "twilight", description: "Late-afternoon discounted slots." },
			{ value: "weekend", description: "Weekend-specific rate." },
			{ value: "weekday", description: "Weekday-specific rate." },
		],
		agent_note: "Pick the closest window. If the document uses time boundaries (\"Tee times 07:30–11:59\"), pick the closest window and note the exact boundary in the line's `notes`. `anytime` is the right default when the rate card has a single unsegmented green-fee price.",
	},
	{
		key: "green_fee_lines[].tee_window_label",
		type: "string | null",
		required: false,
		description: "Original tee-window label/boundary verbatim from the document (e.g. \"Tee times 07:30–11:59\", \"AM rate\", \"Twilight after 15:00\"). Null when the document uses a normalised label that already matches the enum exactly.",
		agent_note: "Capture the supplier's wording verbatim so a reviewer can sanity-check the normalisation. Null only when no extra label exists beyond the enum value itself.",
	},
	{
		key: "green_fee_lines[].period.start",
		type: "ISO date",
		required: true,
		description: "First date the rate applies (inclusive).",
		agent_note: "ISO YYYY-MM-DD. If the rate card uses season labels (\"High season\"), resolve to the most precise boundary stated and note the assumption in the line's `notes`.",
	},
	{
		key: "green_fee_lines[].period.end",
		type: "ISO date",
		required: true,
		description: "Last date the rate applies (inclusive).",
		agent_note: "ISO YYYY-MM-DD, inclusive.",
	},
	{
		key: "green_fee_lines[].month_set",
		type: "integer[]",
		required: true,
		description: "Denormalised calendar months (1..12) that the period touches. Powers the indexer's `months_available[]` hook.",
		agent_note: "Integer array, each value in [1, 12], sorted ascending, no duplicates.",
	},
	{
		key: "green_fee_lines[].price_native",
		type: "number",
		required: true,
		description: "Rate value in the supplier's native currency.",
		agent_note: "Number with up to two decimal places. Do not convert currencies. The price as printed on the rate table.",
	},
	{
		key: "green_fee_lines[].currency",
		type: "string (ISO 4217)",
		required: true,
		description: "ISO 4217 alpha-3 currency code (\"EUR\", \"GBP\", \"USD\").",
		agent_note: "Three-letter uppercase code. Resolve currency symbols (€ → EUR, £ → GBP).",
	},
	{
		key: "green_fee_lines[].price_basis",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "per_round_per_person", description: "Per-round, per-player rate (standard)." },
			{ value: "per_round_per_group", description: "Per-round, per-group rate (rare; for fourball/group bookings billed as a single fee)." },
			{ value: "per_day_per_person", description: "Per-day unlimited-rounds rate, per player." },
			{ value: "per_round_per_buggy", description: "Per-round, per-buggy rate — used only when the line itself prices a buggy (not a player)." },
		],
		agent_note: "Pick from the rate table header. Default to `per_round_per_person` for unstated standard green fees.",
	},
	{
		key: "green_fee_lines[].buggy_included",
		type: "boolean",
		required: true,
		description: "Whether buggy/cart is included in this green-fee rate.",
		agent_note: "Default `false`. Only `true` when the rate card explicitly says buggy is included (\"green fee including shared buggy\").",
	},
	{
		key: "green_fee_lines[].replays_included",
		type: "boolean",
		required: true,
		description: "Whether a same-day replay is included in this rate.",
		agent_note: "Default `false`. Only `true` when the rate card explicitly states replays are included.",
	},
	{
		key: "green_fee_lines[].notes",
		type: "string | null",
		required: false,
		description: "Anything that doesn't fit elsewhere on this line.",
		agent_note: "Tee-time boundaries, assumptions about ambiguous seasons, parenthetical inclusions, course-specific conditions.",
	},
	{
		key: "green_fee_supplements[].kind",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "buggy_hire", description: "Buggy/cart hire when not included in the green fee." },
			{ value: "twilight_discount", description: "Percentage discount applied for twilight tee times." },
			{ value: "replay", description: "Discounted same-day replay rate." },
			{ value: "junior_rate", description: "Discounted rate for juniors (age-banded)." },
			{ value: "caddie", description: "Caddie hire per round." },
			{ value: "club_hire", description: "Club rental per round/day." },
			{ value: "trolley_hire", description: "Trolley (push/electric) hire per round." },
			{ value: "lessons", description: "Coaching/lessons supplement." },
			{ value: "other", description: "Anything that doesn't fit; describe in `notes`." },
		],
		agent_note: "Pick the closest kind. Use `other` when uncertain rather than misclassify.",
	},
	{
		key: "green_fee_supplements[].applies_to",
		type: "string[]",
		required: true,
		description: "Course names (verbatim, matching `green_fee_lines[].course`) this supplement applies to. Empty array means the supplement applies to every course on the doc.",
		agent_note: "Verbatim names. Empty array for blanket supplements (e.g. a flat caddie fee that applies across all courses).",
	},
	{
		key: "green_fee_supplements[].value_native",
		type: "number | null",
		required: false,
		description: "Absolute supplement amount in native currency. Null when percentage-based.",
		agent_note: "Use for fixed amounts. Mutually exclusive with `value_pct`.",
	},
	{
		key: "green_fee_supplements[].value_pct",
		type: "number | null",
		required: false,
		description: "Percentage value (e.g. 30 for a 30% twilight discount).",
		agent_note: "Number, not a fraction (30, not 0.3). Direction (discount vs surcharge) is implicit in `kind` — `twilight_discount` is always a discount.",
	},
	{
		key: "green_fee_supplements[].currency",
		type: "string (ISO 4217) | null",
		required: false,
		description: "Currency for `value_native`. Null when percentage-based.",
		agent_note: "Match the rate-card currency.",
	},
	{
		key: "green_fee_supplements[].basis",
		type: "string (enum)",
		required: true,
		options: [
			{ value: "per_round", description: "Applied per round." },
			{ value: "per_round_per_person", description: "Applied per round per player." },
			{ value: "per_day", description: "Applied per day." },
			{ value: "per_person", description: "Applied per player, billing once per round." },
			{ value: "fixed", description: "Flat amount, basis not relevant." },
		],
		agent_note: "Default to `per_round` for buggy hire (shared), `per_round_per_person` for caddie, `per_round` for trolley.",
	},
	{
		key: "green_fee_supplements[].period.start",
		type: "ISO date | null",
		required: false,
		description: "First date the supplement applies. Null when the supplement applies for the entire rate-card validity.",
		agent_note: "Use only when the supplement is restricted to a sub-period.",
	},
	{
		key: "green_fee_supplements[].period.end",
		type: "ISO date | null",
		required: false,
		description: "Last date the supplement applies (inclusive).",
		agent_note: "Mirror the rules for `green_fee_lines[].period.end`.",
	},
	{
		key: "green_fee_supplements[].age_range",
		type: "string | null",
		required: false,
		description: "Age range for junior supplements, as stated on the document (e.g. \"under 18\", \"12-17\").",
		agent_note: "Verbatim from the document. Null for non-age-banded supplements.",
	},
	{
		key: "green_fee_supplements[].notes",
		type: "string | null",
		required: false,
		description: "Anything that doesn't fit elsewhere on this supplement.",
		agent_note: "Booking restrictions, qualifying conditions, peak-day exclusions.",
	},
	{
		key: "agent_metadata.confidence_score",
		type: "number",
		required: true,
		description: "Calibrated self-confidence in the overall extraction, between 0 and 1.",
		agent_note: "Float in [0, 1] reflecting how confident you are the extracted lines and supplements faithfully represent the rate card.",
	},
	{
		key: "agent_metadata.reasoning",
		type: "string",
		required: true,
		description: "Short rationale describing line/supplement counts and any material assumptions.",
		agent_note: "One short paragraph. Cover (a) how many lines and supplements you emitted and the axes you decomposed across, (b) tee-window normalisation choices, (c) anything you deliberately did not extract because it belonged to the accommodation specialist.",
	},
]

export const GREEN_FEES_FIELD_JSON_SCHEMA_NAME = "supplier_green_fees_extraction"

export const GREEN_FEES_FIELD_JSON_SCHEMA = {
	type: "object",
	additionalProperties: false,
	properties: {
		extraction_id: { type: "string" },
		extracted_at: { type: "string" },
		green_fee_lines: {
			type: "array",
			items: {
				type: "object",
				additionalProperties: false,
				properties: {
					course: { type: "string" },
					rounds: { type: "number" },
					holes: { type: "number" },
					tee_window: {
						type: "string",
						enum: [
							"anytime",
							"morning",
							"afternoon",
							"twilight",
							"weekend",
							"weekday",
						],
					},
					tee_window_label: { type: ["string", "null"] },
					period: {
						type: "object",
						additionalProperties: false,
						properties: {
							start: { type: "string" },
							end: { type: "string" },
						},
						required: ["start", "end"],
					},
					month_set: {
						type: "array",
						items: { type: "number", minimum: 1, maximum: 12 },
					},
					price_native: { type: "number" },
					currency: { type: "string" },
					price_basis: {
						type: "string",
						enum: [
							"per_round_per_person",
							"per_round_per_group",
							"per_day_per_person",
							"per_round_per_buggy",
						],
					},
					buggy_included: { type: "boolean" },
					replays_included: { type: "boolean" },
					notes: { type: ["string", "null"] },
				},
				required: [
					"course",
					"rounds",
					"holes",
					"tee_window",
					"tee_window_label",
					"period",
					"month_set",
					"price_native",
					"currency",
					"price_basis",
					"buggy_included",
					"replays_included",
					"notes",
				],
			},
		},
		green_fee_supplements: {
			type: "array",
			items: {
				type: "object",
				additionalProperties: false,
				properties: {
					kind: {
						type: "string",
						enum: [
							"buggy_hire",
							"twilight_discount",
							"replay",
							"junior_rate",
							"caddie",
							"club_hire",
							"trolley_hire",
							"lessons",
							"other",
						],
					},
					applies_to: {
						type: "array",
						items: { type: "string" },
					},
					value_native: { type: ["number", "null"] },
					value_pct: { type: ["number", "null"] },
					currency: { type: ["string", "null"] },
					basis: {
						type: "string",
						enum: [
							"per_round",
							"per_round_per_person",
							"per_day",
							"per_person",
							"fixed",
						],
					},
					period: {
						type: ["object", "null"],
						additionalProperties: false,
						properties: {
							start: { type: ["string", "null"] },
							end: { type: ["string", "null"] },
						},
						required: ["start", "end"],
					},
					age_range: { type: ["string", "null"] },
					notes: { type: ["string", "null"] },
				},
				required: [
					"kind",
					"applies_to",
					"value_native",
					"value_pct",
					"currency",
					"basis",
					"period",
					"age_range",
					"notes",
				],
			},
		},
		agent_metadata: {
			type: "object",
			additionalProperties: false,
			properties: {
				confidence_score: { type: "number", minimum: 0, maximum: 1 },
				reasoning: { type: "string" },
			},
			required: ["confidence_score", "reasoning"],
		},
	},
	required: [
		"extraction_id",
		"extracted_at",
		"green_fee_lines",
		"green_fee_supplements",
		"agent_metadata",
	],
}

export const GREEN_FEES_FIELD_SCHEMA_EXAMPLE = {
	extraction_id: "7c1a4e02-9b6f-4c1a-bd91-2e8c1f3a7e44",
	extracted_at: "2026-05-13T16:42:00Z",
	green_fee_lines: [
		{
			course: "Faldo Course",
			rounds: 1,
			holes: 18,
			tee_window: "anytime",
			tee_window_label: null,
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			price_native: 145,
			currency: "EUR",
			price_basis: "per_round_per_person",
			buggy_included: false,
			replays_included: false,
			notes: null,
		},
		{
			course: "Faldo Course",
			rounds: 1,
			holes: 18,
			tee_window: "twilight",
			tee_window_label: "Tee times after 15:00",
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			price_native: 95,
			currency: "EUR",
			price_basis: "per_round_per_person",
			buggy_included: false,
			replays_included: false,
			notes: null,
		},
		{
			course: "O'Connor Jnr",
			rounds: 1,
			holes: 18,
			tee_window: "anytime",
			tee_window_label: null,
			period: { start: "2026-04-01", end: "2026-06-30" },
			month_set: [4, 5, 6],
			price_native: 125,
			currency: "EUR",
			price_basis: "per_round_per_person",
			buggy_included: false,
			replays_included: false,
			notes: null,
		},
	],
	green_fee_supplements: [
		{
			kind: "buggy_hire",
			applies_to: [],
			value_native: 35,
			value_pct: null,
			currency: "EUR",
			basis: "per_round",
			period: null,
			age_range: null,
			notes: "Shared buggy. Single-driver buggy €50.",
		},
		{
			kind: "junior_rate",
			applies_to: ["Faldo Course", "O'Connor Jnr"],
			value_native: null,
			value_pct: 50,
			currency: null,
			basis: "per_round_per_person",
			period: null,
			age_range: "under 18",
			notes: null,
		},
	],
	agent_metadata: {
		confidence_score: 0.87,
		reasoning:
			"Emitted 3 lines across 2 courses; Faldo has both anytime and twilight rates, O'Connor only has an anytime rate published. Two supplements: shared-buggy at €35 per round and a 50% junior discount applying to both courses. Twilight rate normalised from \"Tee times after 15:00\" (label preserved). Accommodation rates also appear on this rate card but are deliberately left to the accommodation specialist.",
	},
}
